/**
 * IsConversationMutedUseCase
 *
 * Returns whether the current user has muted notifications for the
 * conversation with the given peer. A conversation is considered muted iff a
 * record is present in encrypted local storage under the
 * `SECURE_STORAGE_KEYS.MUTED_USERS + peerId` key.
 *
 * Flutter source: lib/domain/entity/bottom_sheet/chat_actions_bottom_sheet.dart:127-133 —
 * `getConversationIsMuted()` opens the encrypted `MutedUser` Hive box and
 * returns `box.get(MutedUser.keyName + widget.user.firebaseId) != null`. The
 * predicate is presence-only: any non-null record means muted. The web
 * equivalent lives in `SecureStorageWebCrypto` keyed by the same prefix +
 * peerId.
 *
 * Architectural note: like `IsConversationBlockedUseCase`, this use case
 * touches `SecureStorageWebCrypto` directly because the Flutter side reads
 * Hive in the sheet itself (no data source method exists to mirror).
 */

import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { MutedUserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';

export class IsConversationMutedUseCase {
  private readonly storage = SecureStorageWebCrypto.getInstance();

  /**
   * Check whether the current user has muted the conversation with `peerId`.
   *
   * @param peerId - The peer user's firebase id (Flutter: `widget.user.firebaseId`).
   * @returns `true` iff a `MutedUser` record exists in encrypted storage under
   *   the mute key; `false` otherwise.
   */
  async execute(peerId: string): Promise<boolean> {
    const storageKey = SECURE_STORAGE_KEYS.MUTED_USERS + peerId;
    const record = await this.storage.getEncrypted<MutedUserStorage>(storageKey);
    return record !== null;
  }
}
